/**
 * Render-time markdown stripping for search-result snippets (U7 SEARCH slice,
 * ui-design-review-2026-07-05.md P1 item 2: "snippets show raw markdown
 * (`**`, `#`, backticks)").
 *
 * SNIPPET WIRE SHAPE (inspected 2026-07-05): `ResultItem.snippet` is PLAIN
 * TEXT, not HTML. `<ResultCard>` renders it as a text child (never
 * `dangerouslySetInnerHTML` — enforced by a boundary test), and the backend's
 * two snippet paths both emit unmarked text:
 *   - semantic hits: the raw contributing-chunk text (retrieval/core.py
 *     `_snippet_for`) — i.e. raw markdown from the note body;
 *   - lexical hits: a `ts_headline` excerpt configured with
 *     `StartSel='', StopSel=''` (retrieval/lexical.py) — highlight tags are
 *     explicitly disabled on the wire.
 * So there are NO `<mark>` highlight spans to preserve in the card today;
 * the `.res-snippet mark` CSS is a legacy hook for a future safe-HTML shape.
 * We still unwrap any stray highlight TAGS defensively (keeping their inner
 * text) in case an older/newer backend ever ships them.
 *
 * This is a small pure function (no React/DOM) so it stays unit-testable
 * under the node vitest environment. It is a display transform only — the
 * RAW snippet keeps flowing into the editor-highlight handoff
 * (`goToNote({ quotedText })`), whose own matcher does its normalization.
 */

/**
 * Strip markdown syntax from a plain-text snippet for display.
 * Conservative by design: paired-marker constructs are only unwrapped when
 * the pair is intact (so a lone `*` or snake_case_identifier survives), and
 * all whitespace collapses to single spaces (the card clamps to 3 lines —
 * newlines carry no information there).
 */
export function stripMarkdownSyntax(text: string): string {
  let out = text;

  // Defensive: unwrap highlight/emphasis TAGS if a backend ever ships them
  // (keep inner text). Live corpus snippets DO carry `<br />` (transcript
  // imports) and HTML entities like `&quot;` (email-sourced notes) — seen in
  // the 2026-07-05 verification pass against the real corpus.
  out = out.replace(/<br\s*\/?>/gi, ' ');
  out = out.replace(/<\/?(?:mark|em|strong|b|i)\s*>/gi, '');
  // Decode the common named/numeric entities that survive into snippets.
  out = out
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&');

  // Images before links: ![alt](url) → alt
  out = out.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
  // Links: [text](url) → text
  out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  // Wiki links: [[target]] → target, [[target|label]] → label
  out = out.replace(
    /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g,
    (_m, target: string, label?: string) => label ?? target,
  );

  // Line-leading structure: headings, blockquotes, list bullets (+ task
  // boxes), fence lines, horizontal rules.
  out = out.replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, '');
  out = out.replace(/^[ \t]*>[ \t]?/gm, '');
  out = out.replace(/^[ \t]*(?:[-*+]|\d{1,3}[.)])[ \t]+(?:\[[ xX]\][ \t]+)?/gm, '');
  out = out.replace(/^[ \t]*(?:```|~~~)[^\n]*$/gm, '');
  out = out.replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '');

  // Paired inline markers — bold, italic, strikethrough, inline code.
  // Double markers first so `**x**` doesn't half-strip via the single-`*` rule.
  out = out.replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '$2');
  out = out.replace(/(^|[^\w*])\*(?=\S)([^*\n]*?\S)\*(?![\w*])/g, '$1$2');
  // Underscore italics only when NOT inside a word (snake_case survives).
  out = out.replace(/(^|[^\w_])_(?=\S)([^_\n]*?\S)_(?![\w_])/g, '$1$2');
  out = out.replace(/~~(?=\S)([\s\S]*?\S)~~/g, '$1');
  out = out.replace(/`([^`\n]+)`/g, '$1');

  // Collapse whitespace — the snippet renders as a 3-line-clamped <p>.
  return out.replace(/\s+/g, ' ').trim();
}
